import moment from 'moment'
import type { RangeKey } from '@dataTypes/dashboard.types'
import { DASHBOARD_DETAIL_CONTEXT } from '@constants/Dashboard/dashboardDetailContext.util'
import {
  DashboardRange,
  DashboardTab,
  HuddleDay,
} from '@constants/Dashboard/dashboard.enums'

const CALENDAR_DATE_FORMAT = 'MMMM D, YYYY'

const VALID_DASHBOARD_RANGES = new Set<string>(Object.values(DashboardRange))

function parseIndexRange(
  searchParams: URLSearchParams,
  fallback: RangeKey = '1D',
): RangeKey {
  const range = searchParams.get('range')
  if (range && VALID_DASHBOARD_RANGES.has(range)) return range as RangeKey
  return fallback
}

function parseIndexDate(
  searchParams: URLSearchParams,
  fallbackDisplayDate: string,
): string {
  const date = searchParams.get('date')
  if (date && moment(date, 'YYYY-MM-DD', true).isValid()) {
    return moment(date, 'YYYY-MM-DD').format(CALENDAR_DATE_FORMAT)
  }
  return fallbackDisplayDate
}

function formatIndexDateQuery(displayDate: string): string {
  return moment(displayDate, CALENDAR_DATE_FORMAT).format('YYYY-MM-DD')
}

export type DashboardIndexTab = DashboardTab

export type DashboardHuddleDayTab = HuddleDay

export type DashboardIndexParams = {
  tab: DashboardIndexTab
  day?: DashboardHuddleDayTab
  range?: RangeKey
  date?: string
}

export type DashboardScrollSnapshot = {
  /** Primary scroll position on `.dashboard-main`. */
  scrollTop: number
  /** Fallback scroll on `.page-content` when the outer shell scrolls. */
  pageScrollTop?: number
  sectionHref: string
}

const VALID_DASHBOARD_TABS = new Set<string>(Object.values(DashboardTab))

const VALID_HUDDLE_DAYS = new Set<string>(Object.values(HuddleDay))

const DASHBOARD_SCROLL_SNAPSHOT_KEY = 'dashboard-return-scroll'

const CONTEXT_TO_TAB: Record<string, DashboardIndexTab> = {
  [DASHBOARD_DETAIL_CONTEXT.overview]: DashboardTab.Overview,
  [DASHBOARD_DETAIL_CONTEXT.finance]: DashboardTab.Finance,
  [DASHBOARD_DETAIL_CONTEXT.performance]: DashboardTab.Performance,
  [DASHBOARD_DETAIL_CONTEXT.operations]: DashboardTab.Operations,
  [DASHBOARD_DETAIL_CONTEXT.huddle]: DashboardTab.Huddle,
}

export function isDashboardIndexTab(value: string): value is DashboardIndexTab {
  return VALID_DASHBOARD_TABS.has(value)
}

export function isDashboardHuddleDayTab(
  value: string,
): value is DashboardHuddleDayTab {
  return VALID_HUDDLE_DAYS.has(value)
}

export function resolveDashboardTabFromDetailSearch(
  searchParams: URLSearchParams,
  fallback: DashboardIndexTab = DashboardTab.Overview,
): DashboardIndexTab {
  const tab = searchParams.get('tab')
  if (tab && isDashboardIndexTab(tab)) return tab
  const context = searchParams.get('context')
  if (context && CONTEXT_TO_TAB[context]) return CONTEXT_TO_TAB[context]
  return fallback
}

export function parseDashboardHuddleDay(
  searchParams: URLSearchParams,
  fallback?: DashboardHuddleDayTab,
): DashboardHuddleDayTab | undefined {
  const day = searchParams.get('day')
  if (day && isDashboardHuddleDayTab(day)) return day
  return fallback
}

export function parseDashboardIndexParams(
  searchParams: URLSearchParams,
  fallbacks: {
    tab?: DashboardIndexTab
    day?: DashboardHuddleDayTab
    range?: RangeKey
    date?: string
  } = {},
): DashboardIndexParams {
  const tab = resolveDashboardTabFromDetailSearch(
    searchParams,
    fallbacks.tab ?? DashboardTab.Overview,
  )
  const day = parseDashboardHuddleDay(searchParams, fallbacks.day)
  const range = parseIndexRange(searchParams, fallbacks.range ?? '1D')
  const date = parseIndexDate(
    searchParams,
    fallbacks.date ?? moment().format(CALENDAR_DATE_FORMAT),
  )
  return {
    tab,
    ...(day ? { day } : {}),
    range,
    date,
  }
}

export function buildDashboardIndexHref(
  params: Partial<DashboardIndexParams>,
): string {
  const query = new URLSearchParams()
  if (params.tab) query.set('tab', params.tab)
  if (params.day) query.set('day', params.day)
  if (params.range) query.set('range', params.range)
  if (params.date) {
    query.set('date', formatIndexDateQuery(params.date))
  }
  const qs = query.toString()
  return qs ? `/?${qs}` : '/'
}

export function buildDashboardIndexHrefFromDetailSearch(
  searchParams: URLSearchParams,
): string {
  const tab = resolveDashboardTabFromDetailSearch(searchParams)
  const day = parseDashboardHuddleDay(searchParams)
  const range = searchParams.get('range')
  const date = searchParams.get('date')
  return buildDashboardIndexHref({
    tab,
    ...(day ? { day } : {}),
    ...(range ? { range: range as RangeKey } : {}),
    ...(date
      ? {
          date: parseIndexDate(
            searchParams,
            moment().format(CALENDAR_DATE_FORMAT),
          ),
        }
      : {}),
  })
}

export function patchDashboardIndexSearchParams(
  current: URLSearchParams,
  patch: Partial<DashboardIndexParams>,
): URLSearchParams {
  const next = new URLSearchParams(current)
  if (patch.tab) {
    next.set('tab', patch.tab)
    if (patch.tab !== 'huddle') {
      next.delete('day')
    }
  }
  if (patch.day) next.set('day', patch.day)
  if (patch.range) next.set('range', patch.range)
  if (patch.date) {
    next.set('date', formatIndexDateQuery(patch.date))
  }
  return next
}

export function getDashboardScrollContainer(): HTMLElement | null {
  return (
    (document.querySelector('.dashboard-main') as HTMLElement | null) ??
    (document.querySelector('.page-content') as HTMLElement | null)
  )
}

function scrollSectionIntoDashboardMain(section: HTMLElement): void {
  const dashboardMain = document.querySelector(
    '.dashboard-main',
  ) as HTMLElement | null
  if (!dashboardMain) {
    section.scrollIntoView({ block: 'start', behavior: 'auto' })
    return
  }
  const containerTop = dashboardMain.getBoundingClientRect().top
  const sectionTop = section.getBoundingClientRect().top
  const gap = 8
  dashboardMain.scrollTop = Math.max(
    0,
    dashboardMain.scrollTop + sectionTop - containerTop - gap,
  )
}

function normalizeDashboardHref(href: string): string {
  if (href.startsWith('http://') || href.startsWith('https://')) {
    const url = new URL(href)
    return `${url.pathname}${url.search}`
  }
  if (href.startsWith('/')) return href
  return `/${href}`
}

function hrefsMatch(storedHref: string, linkHref: string): boolean {
  const normalizedStored = normalizeDashboardHref(storedHref)
  const normalizedLink = normalizeDashboardHref(linkHref)
  if (normalizedStored === normalizedLink) return true
  const [storedPath, storedSearch = ''] = normalizedStored.split('?')
  const [linkPath, linkSearch = ''] = normalizedLink.split('?')
  if (storedPath !== linkPath) return false
  const storedParams = new URLSearchParams(storedSearch)
  const linkParams = new URLSearchParams(linkSearch)
  return Array.from(storedParams.entries()).every(
    ([key, value]) => linkParams.get(key) === value,
  )
}

export function findDashboardSectionByHref(
  sectionHref: string,
): HTMLElement | null {
  const links = document.querySelectorAll('a.dashboard-section-title-link')
  const matchedLink = Array.from(links).find((link) => {
    const href = link.getAttribute('href')
    return Boolean(href && hrefsMatch(sectionHref, href))
  })
  return (
    (matchedLink?.closest('.dashboard-section-box') as HTMLElement | null) ??
    null
  )
}

export function captureDashboardScroll(sectionHref: string): void {
  const dashboardMain = document.querySelector(
    '.dashboard-main',
  ) as HTMLElement | null
  const pageContent = document.querySelector(
    '.page-content',
  ) as HTMLElement | null
  const snapshot: DashboardScrollSnapshot = {
    scrollTop: dashboardMain?.scrollTop ?? 0,
    pageScrollTop: pageContent?.scrollTop ?? 0,
    sectionHref,
  }
  try {
    sessionStorage.setItem(
      DASHBOARD_SCROLL_SNAPSHOT_KEY,
      JSON.stringify(snapshot),
    )
  } catch {
    // sessionStorage may be unavailable in private mode.
  }
}

export function consumeDashboardScrollSnapshot(): DashboardScrollSnapshot | null {
  try {
    const raw = sessionStorage.getItem(DASHBOARD_SCROLL_SNAPSHOT_KEY)
    if (!raw) return null
    sessionStorage.removeItem(DASHBOARD_SCROLL_SNAPSHOT_KEY)
    const parsed = JSON.parse(raw) as DashboardScrollSnapshot
    if (
      typeof parsed.scrollTop !== 'number' ||
      typeof parsed.sectionHref !== 'string'
    ) {
      return null
    }
    return parsed
  } catch {
    return null
  }
}

export function restoreDashboardScroll(
  snapshot: DashboardScrollSnapshot,
): boolean {
  const dashboardMain = document.querySelector(
    '.dashboard-main',
  ) as HTMLElement | null
  const pageContent = document.querySelector(
    '.page-content',
  ) as HTMLElement | null

  if (dashboardMain) {
    dashboardMain.scrollTop = snapshot.scrollTop
  }
  if (pageContent && typeof snapshot.pageScrollTop === 'number') {
    pageContent.scrollTop = snapshot.pageScrollTop
  }
  if (snapshot.scrollTop > 0 || (snapshot.pageScrollTop ?? 0) > 0) {
    return true
  }

  const section = findDashboardSectionByHref(snapshot.sectionHref)
  if (section) {
    scrollSectionIntoDashboardMain(section)
    return true
  }
  return false
}

export function restoreDashboardScrollWithRetry(
  snapshot: DashboardScrollSnapshot,
  maxAttempts = 10,
  delayMs = 100,
): void {
  let attempts = 0
  const tryRestore = (): void => {
    const restored = restoreDashboardScroll(snapshot)
    if (restored || attempts >= maxAttempts) return
    attempts += 1
    window.setTimeout(tryRestore, delayMs)
  }
  requestAnimationFrame(tryRestore)
}

export function scrollDashboardDetailToTop(): void {
  window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
  const pageContent = document.querySelector(
    '.page-content',
  ) as HTMLElement | null
  if (pageContent) {
    pageContent.scrollTo({ top: 0, left: 0, behavior: 'auto' })
  }
  const dashboardMain = document.querySelector(
    '.dashboard-main',
  ) as HTMLElement | null
  if (dashboardMain) {
    dashboardMain.scrollTo({ top: 0, left: 0, behavior: 'auto' })
  }
}
